import React, { useState, useEffect, useRef } from 'react';
import { 
  CreditCard, Plus, Edit2, Trash2, X, Save, Search, 
  CheckCircle, XCircle
} from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const PaymentModes = () => {
  // State Management
  const [loading, setLoading] = useState(true);
  const [paymentModes, setPaymentModes] = useState([]);
  const [filteredModes, setFilteredModes] = useState([]);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Form Data
  const [formData, setFormData] = useState({
    paymentmodeid: null,
    paymentmodecode: '',
    paymentmodename: '',
    description: '',
    isactive: 1,
    displayorder: 0
  });
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  
  // Errors
  const [errors, setErrors] = useState({});
  
  // Refs
  const codeRef = useRef(null);

  useEffect(() => {
    loadPaymentModes();
  }, []);

  useEffect(() => {
    filterPaymentModes();
  }, [searchTerm, paymentModes]);

  const loadPaymentModes = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/PaymentModes.php?include_inactive=1');
      
      if (response.data.success) {
        setPaymentModes(response.data.data || []);
      } else {
        alert('Failed to load payment modes');
      }
    } catch (error) {
      console.error('Error loading payment modes:', error);
      alert('Error loading payment modes.');
    } finally {
      setLoading(false);
    }
  };

  const filterPaymentModes = () => {
    let filtered = [...paymentModes];
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(mode => 
        mode.paymentmodecode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mode.paymentmodename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mode.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredModes(filtered);
  };

  const handleAdd = () => {
    setFormData({
      paymentmodeid: null,
      paymentmodecode: '',
      paymentmodename: '',
      description: '',
      isactive: 1,
      displayorder: 0
    });
    setErrors({});
    setEditMode(false);
    setShowModal(true);
    
    setTimeout(() => {
      if (codeRef.current) {
        codeRef.current.focus();
      }
    }, 100);
  };

  const handleEdit = (mode) => {
    setFormData({
      paymentmodeid: mode.paymentmodeid,
      paymentmodecode: mode.paymentmodecode,
      paymentmodename: mode.paymentmodename,
      description: mode.description || '',
      isactive: mode.isactive,
      displayorder: mode.displayorder
    });
    setErrors({});
    setEditMode(true);
    setShowModal(true);
    
    setTimeout(() => {
      if (codeRef.current) {
        codeRef.current.focus();
      }
    }, 100);
  };

  const handleDelete = async (mode) => {
    if (!window.confirm(`Are you sure you want to deactivate "${mode.paymentmodename}"?`)) {
      return;
    }
    
    try {
      const response = await apiClient.delete(`/PaymentModes.php?id=${mode.paymentmodeid}`);
      
      if (response.data.success) {
        alert('Payment mode deactivated successfully!');
        loadPaymentModes();
      } else {
        alert(response.data.message || 'Failed to deactivate payment mode');
      }
    } catch (error) {
      console.error('Error deactivating payment mode:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleToggleActive = async (mode) => {
    const newStatus = mode.isactive === 1 ? 0 : 1;
    
    try {
      const response = await apiClient.put('/PaymentModes.php', {
        paymentmodeid: mode.paymentmodeid,
        isactive: newStatus
      });
      
      if (response.data.success) {
        alert(`Payment mode ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        loadPaymentModes();
      } else {
        alert(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.paymentmodecode.trim()) {
      newErrors.paymentmodecode = 'Payment mode code is required';
    }
    
    if (!formData.paymentmodename.trim()) {
      newErrors.paymentmodename = 'Payment mode name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const payload = {
        paymentmodecode: formData.paymentmodecode.trim().toUpperCase(),
        paymentmodename: formData.paymentmodename.trim(),
        description: formData.description.trim(),
        isactive: formData.isactive,
        displayorder: parseInt(formData.displayorder)
      };
      
      let response;
      if (editMode) {
        payload.paymentmodeid = formData.paymentmodeid;
        response = await apiClient.put('/PaymentModes.php', payload);
      } else {
        response = await apiClient.post('/PaymentModes.php', payload);
      }
      
      if (response.data.success) {
        alert(`Payment mode ${editMode ? 'updated' : 'created'} successfully!`);
        setShowModal(false);
        loadPaymentModes();
      } else {
        alert(response.data.message || `Failed to ${editMode ? 'update' : 'create'} payment mode`);
      }
    } catch (error) {
      console.error('Error saving payment mode:', error);
      const errorMessage = error.response?.data?.message || error.message;
      
      if (errorMessage.includes('already exists')) {
        setErrors(prev => ({
          ...prev,
          paymentmodecode: 'Payment mode code already exists'
        }));
      } else {
        alert('Error saving payment mode: ' + errorMessage);
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="master-container">
        <div className="loading-state">Loading Payment Modes...</div>
      </div>
    );
  }

  return (
    <div className="master-container">
      {/* Header */}
      <div className="master-header">
        <div className="header-left">
          <h2>
            <CreditCard size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            Payment Modes Management
          </h2>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleAdd}>
            <Plus size={18} />
            Add Payment Mode
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="search-filter-container">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by code, name, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Payment Mode Name</th>
              <th>Description</th>
              <th className="text-center">Display Order</th>
              <th className="text-center">Status</th>
              <th className="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredModes.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  {paymentModes.length === 0 ? (
                    <>
                      <CreditCard size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                      <div>No payment modes found. Click "Add Payment Mode" to create one.</div>
                    </>
                  ) : (
                    <>
                      <Search size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                      <div>No payment modes match your search.</div>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              filteredModes.map((mode) => (
                <tr key={mode.paymentmodeid} className={mode.isactive === 0 ? 'inactive-row' : ''}>
                  <td>
                    <span className="code-badge">{mode.paymentmodecode}</span>
                  </td>
                  <td>
                    <span className="mode-name">{mode.paymentmodename}</span>
                  </td>
                  <td>
                    <span className="description-text">{mode.description || '-'}</span>
                  </td>
                  <td className="text-center">
                    <span className="order-badge">{mode.displayorder}</span>
                  </td>
                  <td className="text-center">
                    <button
                      className={`status-badge ${mode.isactive === 1 ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleActive(mode)}
                      title={mode.isactive === 1 ? 'Click to deactivate' : 'Click to activate'}
                    >
                      {mode.isactive === 1 ? (
                        <>
                          <CheckCircle size={14} />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle size={14} />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(mode)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    {mode.isactive === 1 && (
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(mode)}
                        title="Deactivate"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <CreditCard size={24} />
                {editMode ? 'Edit Payment Mode' : 'Add New Payment Mode'}
              </h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="paymentmodecode">
                    Code <span className="required">*</span>
                  </label>
                  <input
                    ref={codeRef}
                    type="text"
                    id="paymentmodecode"
                    value={formData.paymentmodecode}
                    onChange={(e) => handleInputChange('paymentmodecode', e.target.value.toUpperCase())}
                    className={errors.paymentmodecode ? 'error' : ''}
                    placeholder="e.g., CASH, CARD, UPI"
                    maxLength={50}
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.paymentmodecode && (
                    <span className="error-message">{errors.paymentmodecode}</span>
                  )}
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Unique code for the payment mode (auto-uppercase)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentmodename">
                    Payment Mode Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="paymentmodename"
                    value={formData.paymentmodename}
                    onChange={(e) => handleInputChange('paymentmodename', e.target.value)}
                    className={errors.paymentmodename ? 'error' : ''}
                    placeholder="e.g., Cash Payment, Credit Card"
                    maxLength={100}
                  />
                  {errors.paymentmodename && (
                    <span className="error-message">{errors.paymentmodename}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="displayorder">
                    Display Order
                  </label>
                  <input
                    type="number"
                    id="displayorder"
                    value={formData.displayorder}
                    onChange={(e) => handleInputChange('displayorder', e.target.value)}
                    min={0}
                    placeholder="0"
                  />
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>
                    Order in which payment mode appears in lists (lower numbers first)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="description">
                    Description
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Optional description or notes"
                    rows={3}
                    maxLength={500}
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label" style={{ marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.isactive === 1}
                      onChange={(e) => handleInputChange('isactive', e.target.checked ? 1 : 0)}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  <Save size={18} />
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .code-badge {
          padding: 4px 10px;
          background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
          color: white;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          font-family: 'Courier New', monospace;
          letter-spacing: 0.5px;
        }

        .mode-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 15px;
        }

        .description-text {
          color: #6b7280;
          font-size: 14px;
        }

        .order-badge {
          display: inline-block;
          padding: 4px 12px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-weight: 600;
          color: #4b5563;
          font-size: 14px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .status-badge.active {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.active:hover {
          background: #a7f3d0;
        }

        .status-badge.inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        .status-badge.inactive:hover {
          background: #fecaca;
        }

        .inactive-row {
          opacity: 0.6;
          background-color: #f9fafb;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }

        .checkbox-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .text-center {
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default PaymentModes;